<template>
  <div class="stock-slip-product">
    <b-row class="mb-5">
      <b-col cols="2" class="pr-0">
        <b-form-select
          class="select-style"
          v-model="productSearch.searchType"
          :options="optionProductSearchType"
          size="sm"
          value-field="id"
          text-field="name"
          :disabled="!storeId"
        ></b-form-select>
      </b-col>
      <b-col cols="8" class="pl-0">
        <treeselect
          :load-options="loadOptionProducts"
          :multiple="false"
          :async="true"
          :placeholder="productSearch.placeHolder"
          :cache-options="false"
          retryText="Thử lại..."
          retryTitle="Nhấp thử lại"
          searchPromptText="Nhập để tìm kiếm..."
          loadingText="Đang tải..."
          noResultsText="Không có kết quả"
          openDirection="bottom"
          @select="onSelectProduct"
          :always-open="isOpenProductSearch"
          @search-change="onSearchChange"
          :disabled="!storeId || !editable"
        >
          <label
            slot="option-label"
            slot-scope="{ node, labelClassName }"
            :class="labelClassName"
          >
            <template>
              <div style="display: flex; align-items: center; gap: 8px">
                <!-- Hình ảnh sản phẩm -->
                <ProductImage
                  :src="node.raw.productImageUrl"
                  style="width: 40px; height: 40px; object-fit: contain"
                />

                <!-- Thông tin sản phẩm -->
                <div>
                  {{ node.raw.label }}
                  <span
                    v-if="node.raw.imeiNo && node.raw.imeiNo.trim().length > 0"
                    class="text-warning"
                  >
                    - IMEI: {{ node.raw.imeiNo }}
                  </span>
                </div>
              </div>
            </template>
          </label>
        </treeselect>
      </b-col>
      <b-col cols="2">
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-secondary font-weight-bolder btn-sm"
            @click="showModalImport"
            :disabled="!editable || !storeId"
          >
            <i style="font-size: 1rem" class="far fa-file-excel"></i>
            Nhập từ excel
          </button>
        </div>
      </b-col>
    </b-row>

    <table class="table table-bordered table-hover">
      <thead>
        <tr>
          <th class="header-color text-center">STT</th>
          <th class="header-color text-center">Sản phẩm</th>
          <th scope="col" class="title-center">IMEI</th>
          <th class="header-color text-center">SL</th>
          <th v-if="editable"></th>
        </tr>
      </thead>
      <tbody v-for="(productItem, index) in productItems" :key="index">
        <tr class="heighRow">
          <td class="text-center" style="width: 5%">
            <span class="font-weight-bolder">{{ productItem.stt }}</span>
          </td>
          <td style="width: 30%">
            <ProductCommonInfo :product="productItem" />
          </td>
          <td
            class="stock-slip-imei-td"
            style="width: 25%"
            ref="imeiTd"
            :class="{ 'imei-error-cell': hasImeiError(productItem) }"
          >
            <div
              v-if="
                editable && productItem.productType === productType.PRODUCT_IMEI
              "
            >
              <IMEIInputForm
                :productItem="productItem"
                :storeId="storeId"
                :showChoose="true"
                :statusViews="[1, 4]"
              />
            </div>
            <div v-else class="stock-slip-imei-readonly">
              {{ formatImeiListForDisplay(productItem) }}
            </div>
            <!-- Error message for IMEI products -->
            <div
              v-if="hasImeiError(productItem)"
              class="text-danger mt-1"
              style="font-size: 11px; font-weight: 500"
            >
              <i class="fas fa-exclamation-triangle mr-1"></i>
              Vui lòng nhập IMEI cho sản phẩm này
            </div>
          </td>
          <td class="td-middle text-right" style="width: 10%">
            <div v-if="editable">
              <BInputNumber
                v-if="editable"
                v-model="productItem.quantity"
                style="border-color: #007bff"
                placeholder="Số lượng"
                :inputClass="'w-100 text-right'"
                :disabled="
                  !editable ||
                  productItem.productType === productType.PRODUCT_IMEI
                "
              />
            </div>
            <div v-else>
              {{ productItem.quantity }}
            </div>
          </td>
          <td class="td-middle" style="width: 5%" v-if="editable">
            <div class="d-flex justify-content-center">
              <span
                style="color: #3f4254; font-size: 12px; cursor: pointer"
                @click="showDeleteAlert(productItem)"
              >
                <i
                  style="font-size: 1rem; color: #d33"
                  class="flaticon2-rubbish-bin-delete-button"
                ></i>
              </span>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <th style="text-align: end" colspan="3">Tổng :</th>
          <td style="text-align: right; padding: 0px 20px; font-weight: 600">
            {{ totalByField('quantity') }}
          </td>
        </tr>
      </tfoot>
    </table>
    <ImportStockSlipProductModal
      ref="importProductModal"
      :storeId="storeId"
      v-on:validData="validData"
    />
  </div>
</template>

<script>
import {
  createDebouncedProductSearchExecutor,
  searchByImei,
  searchProducts,
  unwrapProductSearchData,
} from '@/utils/productSearch';
import Treeselect from '@riophae/vue-treeselect';
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import { ASYNC_SEARCH } from '@riophae/vue-treeselect';
import { PRODUCT_TYPE } from '@/utils/enum';
import Swal from 'sweetalert2';
import { makeToastFaile, cloneDeep, convertPrice } from '@/utils/common';
import ProductImage from '@/view/components/products/ProductImage.vue';
import _sumBy from 'lodash/sumBy';
import IMEIInputForm from './IMEIInputForm.vue';
import ProductCommonInfo from '@/view/components/products/ProductCommonInfo.vue';
import ImportStockSlipProductModal from './import-excels/ImportStockSlipProductModal.vue';
import { formatImeiListForDisplay } from '@/utils/stockSlipImeiDisplay';

const SEARCH_TYPE = {
  PRODUCT: 1,
  IMEI: 2,
};

export default {
  props: ['editable', 'productItems', 'storeId', 'isSubmitting', 'isExport'],
  data() {
    return {
      optionProductSearchType: [
        {
          id: 1,
          name: 'Tìm sản phẩm',
        },
        {
          id: 2,
          name: 'Tìm sản phẩm IMEI',
        },
      ],
      productSearch: {
        searchType: SEARCH_TYPE.PRODUCT,
        placeHolder: 'Tìm kiếm theo tên, mã, mã vạch sản phẩm',
        searchName: null,
      },
      isOpenProductSearch: false,
      productSearchTemp: null,
      productType: PRODUCT_TYPE,
      showValidationErrors: false,
    };
  },
  components: {
    Treeselect,
    BInputNumber,
    IMEIInputForm,
    ProductImage,
    ProductCommonInfo,
    ImportStockSlipProductModal,
  },
  computed: {},
  watch: {
    isSubmitting(newVal) {
      if (newVal) {
        this.validateImeiProducts();
      }
    },
  },
  created() {
    this._productSearchLoader = createDebouncedProductSearchExecutor(async (searchQuery, options) => {
      const type = this.productSearch.searchType;
      return this.searchProductAPI(searchQuery, type, options);
    });
  },
  beforeDestroy() {
    this._productSearchLoader?.cancel?.();
  },
  mounted() {},
  methods: {
    formatImeiListForDisplay,
    convertPrice,
    loadOptionProducts({ action, searchQuery, callback }) {
      if (action !== ASYNC_SEARCH) return;
      this.productSearchTemp = searchQuery;
      this._productSearchLoader.run(searchQuery, (error, result) => {
        if (error) {
          callback(error, []);
          return;
        }
        const type = this.productSearch.searchType;
        const optionProduct = (result?.data || []).map((element) => {
          if (type === 1) {
            return {
              id: this.$uuid.v4(),
              label: element.productName,
              ...element,
            };
          }
          return {
            id: this.$uuid.v4(),
            label: element.productName,
            imeiNo: element.imeiNo,
            ...element,
          };
        });
        callback(null, optionProduct);
      });
    },
    onSearchChange(searchQuery) {
      if (searchQuery.length > 1 && !this.isOpenProductSearch) {
        this.isOpenProductSearch = true;
      } else if (searchQuery.length === 0) {
        this.isOpenProductSearch = false;
      }
    },
    async searchProductAPI(searchQuery, type, options = {}) {
      switch (type) {
        case SEARCH_TYPE.PRODUCT:
          return this.fetchProduct(searchQuery, options);
        case SEARCH_TYPE.IMEI:
          return this.fetchProductImei(searchQuery, options);
        default:
          return { data: [] };
      }
    },
    async fetchProduct(textSearch, options = {}) {
      this.listProduct = [];
      const params = {
        searchText: textSearch,
        storeId: this.storeId,
        inStock: true,
        stockType: this.isExport ? 'errorQuantity' : 'inStockQuantity',
      };
      const response = await searchProducts(params, options);
      return { data: unwrapProductSearchData(response) };
    },
    async fetchProductImei(textSearch, options = {}) {
      this.listProduct = [];
      if (!this.storeId) {
        makeToastFaile('Chưa chọn cửa hàng');
        return { data: [] };
      }
      const params = {
        searchText: textSearch,
        storeId: this.storeId,
        status: this.isExport ? 4 : 1,
      };
      const response = await searchByImei(params, options);
      return { data: unwrapProductSearchData(response) };
    },
    onSelectProduct(node) {
      this.isOpenProductSearch = false;
      let countStt = this.productItems.length;

      const item = this.handleProductItemSelected(node, ++countStt);
      this.productItems.push(item);
    },
    handleProductItemSelected(item, stt) {
      const itemSelected = cloneDeep(item);

      itemSelected.quantity = 1;
      itemSelected.inStockQuantity = item.inStockQuantity;
      itemSelected.stt = stt;
      itemSelected.IMEI = item.imeiNo || '';
      return itemSelected;
    },
    async showDeleteAlert(product) {
      const text = `Bạn muốn xoá sản phẩm ${product.productName} ra khỏi danh sách`;
      const alert = await this.showWarningAlert(text);

      if (alert.isConfirmed) {
        const index = this.getIndexProductItems(product.id);

        if (index > -1) {
          this.productItems.splice(index, 1);
          this.numberedProductItems();
        }
      }
    },
    showWarningAlert: function (text) {
      return Swal.fire({
        title: 'Xoá sản phẩm!',
        text: text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Đồng ý',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        showClass: {
          popup: 'animate__animated animate__fadeInDown',
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp',
        },
      });
    },
    getIndexProductItems(id) {
      const index = this.productItems.findIndex((item) => item.id === id);
      if (index < 0) {
        makeToastFaile(`Không tìm thấy ${id} trong danh sách`);
        throw new Error(`Không tìm thấy ${id} trong danh sách`);
      }
      return index;
    },
    numberedProductItems() {
      let count = 1;
      return this.productItems.map((item) => {
        item.stt = count;
        count++;
        return item;
      });
    },
    totalByField(fieldName) {
      return _sumBy(this.productItems, fieldName);
    },
    validateImeiProducts() {
      this.showValidationErrors = true;
      const invalidProducts = this.productItems.filter(
        (productItem) =>
          productItem.productType === this.productType.PRODUCT_IMEI &&
          (!productItem.IMEI || productItem.IMEI.trim() === ''),
      );

      if (invalidProducts.length > 0) {
        return false;
      }

      return true;
    },
    hasImeiError(productItem) {
      return (
        this.showValidationErrors &&
        productItem.productType === this.productType.PRODUCT_IMEI &&
        (!productItem.IMEI || productItem.IMEI.trim() === '')
      );
    },
    showModalImport() {
      this.$refs.importProductModal.showModal();
    },
    validData(data) {
      const currentIndex = this.productItems.length;
      const stockSlipProducts = data.map((item, index) => {
        const imeiString = item.productImei || '';
        const imeiList = imeiString
          ? imeiString
              .split(',')
              .map((imei) => imei.trim())
              .filter((imei) => imei)
          : [];

        // Nếu có IMEI, quantity = số lượng IMEI, ngược lại dùng quantity từ Excel hoặc mặc định là 1
        const quantity = imeiList.length ? imeiList.length : item.quantity || 1;

        return {
          ...item,
          stt: index + currentIndex + 1,
          quantity,
          IMEI: imeiString,
        };
      });
      this.productItems.push(...stockSlipProducts);
      this.numberedProductItems();
    },
  },
};
</script>

<style scoped>
.stock-slip-imei-td {
  vertical-align: top;
}
.stock-slip-imei-readonly {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
  max-height: 220px;
  overflow-y: auto;
  font-size: 12px;
  line-height: 1.35;
}
.imei-error-cell {
  border: 1px solid #ff4d4f !important;
  border-radius: 8px;
  background: #fff1f0;
  transition:
    border 0.2s,
    background 0.2s;
}
</style>
